import { useFormikContext } from "formik";
import React, { useCallback } from "react";

import { ConfirmRecurringModal } from "@components/accounting/journalEntryDrawer/ConfirmRecurringModal";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useDeleteJournalEntry } from "@mutations/accounting";
import { JournalEntryDrawerFormikConfig } from "./JournalEntryDrawer";

interface Props {
    onBack: () => void;
    onDeleteSuccess: () => void;
    onHide?: () => void;
    show?: boolean;
    disableAnimation?: boolean;
}

export const JournalEntryDrawerDelete: React.FC<Props> = ({
    onBack,
    onDeleteSuccess,
    onHide,
    show,
    disableAnimation,
}) => {
    const formikContext = useFormikContext<JournalEntryDrawerFormikConfig>();
    const { values, resetForm } = formikContext;

    const onDelete = useDeleteJournalEntry({
        journalEntryId: values.journalEntryId,
        entityId: values.entityId,
        onSuccess: () => {
            onDeleteSuccess();
            resetForm();
        },
    });

    const onConfirmWithoutRecurrence = useCallback(
        () =>
            onDelete.mutateAsync({
                recurringRuleUpdateType: undefined,
                ledgerBasis: values.ledgerBasis,
                ledgerId: values.ledgerId,
            }),
        [onDelete, values.ledgerBasis, values.ledgerId],
    );

    const showRecurringOptionsModal = !!values.journalEntry.recurringRule;
    return showRecurringOptionsModal ? (
        <ConfirmRecurringModal
            loading={onDelete.isPending}
            title="You're deleting a recurring journal entry"
            subtitle="Please select what you'd like to delete."
            show={show}
            onSubmit={async (recurringRuleUpdateType) => {
                await onDelete.mutateAsync({
                    ledgerBasis: values.ledgerBasis,
                    ledgerId: values.ledgerId,
                    recurringRuleUpdateType:
                        recurringRuleUpdateType ?? undefined,
                });
                onBack();
            }}
            onCancel={onBack}
        />
    ) : (
        <ConfirmationModal
            title="Are you sure that you want to delete this journal entry?"
            question=""
            no="Back"
            onReject={onBack}
            onConfirm={onConfirmWithoutRecurrence}
            onHide={onHide}
            show={show}
            disableAnimation={disableAnimation}
        />
    );
};
